class VariantUpdater {
  static VARIANTS_METAFIELDS_CACHE = null;
  static VARIANT_DYNAMIC_CONTENT_CACHE = new Map();
  static ACTIVE_VARIANT_ID = null;

  // Update SEO metadata to match the selected variant
  static async updateSEOInformation(
    variantInformation,
    variantDefaults,
    variantId
  ) {
    const metaTitle =
      variantInformation["meta_title"] || variantDefaults["meta_title"];
    const metaDescription =
      variantInformation["meta_description"] ||
      variantDefaults["meta_description"];

    document.title = metaTitle;
    const metaDescriptionElement = document.querySelector(
      'meta[name="description"]'
    );
    if (metaDescriptionElement) {
      metaDescriptionElement.setAttribute("content", metaDescription);
    }
  }

  // Update breadcrumb collection information for the selected variant
  static async updateBreadcrumb(variantInformation, productInfo) {
    const collectionTitle =
      variantInformation["breadcrumb"]["name"] ??
      productInfo["breadcrumb"]["name"];
    const collectionUrl =
      variantInformation["breadcrumb"]["url"] ??
      productInfo["breadcrumb"]["url"];

    if (collectionTitle) {
      const $breadcrumbs = document.querySelectorAll("[breadcrumb-collection]");
      if (collectionTitle) {
        $breadcrumbs.forEach(($breadcrumb) => {
          $breadcrumb.innerText = collectionTitle;
          $breadcrumb.setAttribute("href", collectionUrl);
          $breadcrumb.parentNode.style.removeProperty("display");
        });
      } else {
        $breadcrumbs.forEach(($breadcrumb) => {
          $breadcrumb.parentNode.style.diplay = "none";
        });
      }
    }
  }

  // Update dynamic blocks (accordions, description, etc) for the selected variant
  static async updateVariantDynamicMetafields(variantId, productInfo) {
    const productPath = productInfo?.handle
      ? `/products/${productInfo.handle}`
      : document.location.pathname;
    if (!variantId || !productPath) {
      return;
    }
    if (!document.querySelector("[data-variant-dynamic-block]")) {
      return;
    }
    const contentMap = await VariantUpdater.getVariantDynamicContentMap(
      variantId,
      productPath
    );
    if (!contentMap || VariantUpdater.ACTIVE_VARIANT_ID !== variantId) {
      return;
    }
    VariantUpdater.applyVariantDynamicContent(contentMap);
  }

  // Apply dynamic HTML to each matching block in the current DOM
  static applyVariantDynamicContent(contentMap) {
    const dynamicBlocks = document.querySelectorAll(
      "[data-variant-dynamic-block]"
    );
    if (!dynamicBlocks.length) {
      return;
    }
    dynamicBlocks.forEach((block) => {
      const blockType = block.dataset.variantDynamicBlock;
      if (
        !blockType ||
        !Object.prototype.hasOwnProperty.call(contentMap, blockType)
      ) {
        return;
      }
      const targetSelector = block.dataset.variantDynamicTarget;
      const targetElement = targetSelector
        ? block.querySelector(targetSelector)
        : block;
      if (!targetElement) {
        return;
      }
      targetElement.innerHTML = contentMap[blockType] ?? "";
    });
  }

  // Return cached HTML for dynamic blocks or fetch it once per variant
  static async getVariantDynamicContentMap(variantId, productPath) {
    const cacheKey = `${productPath}:${variantId}`;
    const cached = VariantUpdater.VARIANT_DYNAMIC_CONTENT_CACHE.get(cacheKey);
    if (cached) {
      return cached;
    }
    const fetchPromise = VariantUpdater.fetchVariantDynamicContentMap(
      variantId,
      productPath
    ).catch((error) => {
      VariantUpdater.VARIANT_DYNAMIC_CONTENT_CACHE.delete(cacheKey);
      console.warn("Failed to fetch dynamic content", error);
      return null;
    });
    VariantUpdater.VARIANT_DYNAMIC_CONTENT_CACHE.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  // Fetch product HTML for a variant and extract dynamic blocks
  static async fetchVariantDynamicContentMap(variantId, productPath) {
    const fetchUrl = new URL(productPath, window.location.origin);
    fetchUrl.searchParams.set("variant", variantId);
    const viewParam = new URLSearchParams(window.location.search).get("view");
    if (viewParam) fetchUrl.searchParams.set("view", viewParam);
    const response = await fetch(fetchUrl.toString(), {
      credentials: "same-origin",
    });
    if (!response.ok) {
      console.warn(
        `Dynamic content fetch failed: ${response.status} ${response.statusText}`
      );
      return null;
    }
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const contentMap = {};
    const dynamicBlocks = doc.querySelectorAll("[data-variant-dynamic-block]");
    dynamicBlocks.forEach((block) => {
      const blockType = block.dataset.variantDynamicBlock;
      if (!blockType) {
        return;
      }
      const targetSelector = block.dataset.variantDynamicTarget;
      const targetElement = targetSelector
        ? block.querySelector(targetSelector)
        : block;
      if (!targetElement) {
        return;
      }
      contentMap[blockType] = targetElement.innerHTML;
    });
    return contentMap;
  }

  // Update all variant-specific metadata and dynamic content
  static updateVariantMetaInformation(variantId) {
    if (!variantId) {
      console.warn(
        "updateVariantMetaInformation(n: variantId is not provided or invalid."
      );
      return; // Early exit if variantId is null, undefined, or invalid
    }
    VariantUpdater.ACTIVE_VARIANT_ID = variantId;

    VariantUpdater.getVariantsMetaInformation()
      .then((variantsMetafields) => {
        const variantInformation = variantsMetafields[variantId];
        const variantDefaults = variantsMetafields["default"];
        const product = variantsMetafields["product"];

        // SEO Metadata
        VariantUpdater.updateSEOInformation(
          variantInformation,
          variantDefaults,
          variantId
        );

        // Dynamic Variant blocks
        VariantUpdater.updateBreadcrumb(variantInformation, product);
        // Handles all dynamic sections of the variant
        VariantUpdater.updateVariantDynamicMetafields(variantId, product);
      })
      .catch((error) => {
        throw error;
      });
  }

  // Read variants metafields JSON from inline or section fetch
  static async getVariantsMetaInformation() {
    if (VariantUpdater.VARIANTS_METAFIELDS_CACHE !== null) {
      return VariantUpdater.VARIANTS_METAFIELDS_CACHE;
    }
    const inlineMeta = document.getElementById("variants-metafields-json");
    if (inlineMeta) {
      try {
        const parsed = JSON.parse(inlineMeta.textContent);
        // If the markup wrapper is present, unwrap the inner div content
        if (parsed && typeof parsed === "string") {
          VariantUpdater.VARIANTS_METAFIELDS_CACHE = JSON.parse(parsed);
        } else {
          VariantUpdater.VARIANTS_METAFIELDS_CACHE = parsed;
        }
        return VariantUpdater.VARIANTS_METAFIELDS_CACHE;
      } catch (e) {
        console.warn("Failed to parse inline variants metafields JSON", e);
      }
    }
    return fetch(document.location.pathname + "?sections=variants-metafields")
      .then((res) => res.json())
      .then((sections) => {
        if (sections["variants-metafields"]) {
          const container = document.createElement("div");
          container.innerHTML = sections["variants-metafields"];
          VariantUpdater.VARIANTS_METAFIELDS_CACHE = JSON.parse(
            container.querySelector("div").innerText
          );
        }
        return VariantUpdater.VARIANTS_METAFIELDS_CACHE;
      });
  }

  static async updateSelectedBundle(variantId, variantInfo){
    const $selectedOption = document.querySelector('[bundle-option-selected]');
    if($selectedOption){
      $selectedOption.innerText = variantInfo.option1?.replaceAll(' - ', ', ');
    }
  }

  static async updateAccessibility(variantInfo) {
    if(!variantInfo || !variantInfo.title ){
      return false;
    }
    const productTitle = document.querySelector('.product__title').innerText;
    const $addToCartButton = document.querySelector('.product-form__cart-submit');
    const price = parseFloat(variantInfo.price)/100;
    const ariaLabel = `add to cart ${productTitle} ${variantInfo.option1} $${price}`;
    $addToCartButton?.setAttribute('aria-label', ariaLabel);
    //Pop up button
    document.querySelector('.sticky-atc-bar__inner .product-form__cart-submit')?.setAttribute('aria-label', ariaLabel);
  }
}
